export type Role = "user" | "assistant";

/** Modo de resposta: linguagem de negócio ou técnica. */
export type AssistantMode =
  | "business"
  | "technical"
  | "support"
  | "executive"
  | "teaching"
  | "concise"
  | "structured";

/** Motor/CLI por trás da conversa. */
export type AgentKind = "claude" | "codex" | "gemini";

/** sysadmin = operador da plataforma (cross-org); admin = admin de uma org; user = membro. */
export type UserRole = "sysadmin" | "admin" | "user";
export type UserStatus = "pending" | "approved" | "rejected" | "inactive";

/** Papel privilegiado (admin de org ou sysadmin). */
export function isPrivileged(role: UserRole): boolean {
  return role === "admin" || role === "sysadmin";
}

/** Workspace de uma org (multi-tenant). */
export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  orgId: string;
}

export const CUSTOMIZABLE_SHORTCUT_ACTIONS = [
  "search",
  "newConversation",
  "shortcutsHelp",
  "toggleFocusMode",
  "commandPalette",
] as const;
export type ShortcutAction = (typeof CUSTOMIZABLE_SHORTCUT_ACTIONS)[number];

export interface UserSettings {
  defaultMode: AssistantMode;
  defaultModel: string;
  shortcuts?: Partial<Record<ShortcutAction, string>>;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  /** Org (tenant) da pessoa neste app. */
  orgId?: string;
  approvedAt?: string;
  settings: UserSettings;
  /** Modos que esta pessoa tem permissão de usar (controlado pelo admin). */
  allowedModes: AssistantMode[];
  /** Modelos que esta pessoa tem permissão de usar (controlado pelo admin). */
  allowedModels: string[];
  /** Motores (claude/codex) que esta pessoa tem permissão de usar (controlado pelo admin). */
  allowedAgents: AgentKind[];
  twoFactorEnabled: boolean;
  lastSeenChangelogAt: string | null;
  /** Foto de perfil (data URL) desta pessoa neste app. Ausente = inicial do nome. */
  avatarDataUrl: string | null;
  /** Tour de onboarding do 1º login concluído/dispensado. */
  onboardingDone: boolean;
}

export interface AdminUserRow extends AuthUser {
  /** Webhook desta pessoa - destino do link de redefinicao de senha. */
  webhookUrl: string | null;
  sessionCount: number;
  totalTokens: number;
  totalCostUsd: number;
  /** Última utilização (conversa mais recente) - ordenação "últimos utilizadores". */
  lastActivityAt: string | null;
  /** Nota interna do admin/sysadmin sobre esta conta - nunca vista pela própria pessoa. */
  adminNote: string;
}

export interface ActivityEntry {
  ts: string;
  userId: string;
  userEmail: string;
  type:
    | "register"
    | "login"
    | "login_failed"
    | "login_blocked"
    | "logout"
    | "ask"
    | "regenerate"
    | "docgen"
    | "approve"
    | "reject"
    | "deactivate"
    | "view_other"
    | "blocked";
  sessionId?: string;
  model?: string;
  mode?: AssistantMode;
  preview?: string;
  tokens?: number;
  costUsd?: number;
  detail?: string;
  sessionTitle?: string;
  ownerEmail?: string;
}

export interface TurnUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
}

export type TurnFeedback = "like" | "dislike";

export interface TurnVersion {
  content: string;
  ts: string;
  usage?: TurnUsage;
  costUsd?: number;
  durationMs?: number;
}

/** Origem de uma conversa/input: "web" = painel; "api" = via API key. */
export type ConversationSource = "web" | "api";

export interface Turn {
  role: Role;
  content: string;
  ts: string;
  errored?: boolean;
  errorMessage?: string;
  aborted?: boolean;
  usage?: TurnUsage;
  costUsd?: number;
  durationMs?: number;
  feedback?: TurnFeedback;
  versions?: TurnVersion[];
  versionIdx?: number;
  source?: ConversationSource;
  /** Soft delete da mensagem - some pro dono; admin vê marcada. */
  deletedAt?: string | null;
  /** Motor (claude/codex) que gerou ESTA resposta. */
  agent?: AgentKind;
  /** Ferramentas usadas nesta resposta - o backend SÓ envia pra sysadmin. */
  tools?: Array<{ name: string; input: unknown }>;
}

export interface Session {
  id: string;
  turns: Turn[];
  createdAt: string;
  updatedAt: string;
  title?: string;
  pinned?: boolean;
  model?: string;
  mode?: AssistantMode;
  shareId?: string;
  sharedAt?: string | null;
  tags?: string[];
  assignedTo?: string | null;
  summary?: string;
  summarizedAt?: string | null;
  privateNote?: string;
  source?: ConversationSource;
  /** Se a pessoa logada pode gerenciar (não só ler) esta conversa - controla a visibilidade da nota privada. */
  canManage?: boolean;
}

export interface SessionSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  turnCount: number;
  totalTokens?: number;
  totalCostUsd?: number;
  pinned?: boolean;
  model?: string;
  source?: ConversationSource;
  tags?: string[];
  assignedTo?: string | null;
  assignedToEmail?: string | null;
  matchSnippet?: string;
  /** Conversa soft-deletada - só aparece na visão de admin, marcada "apagada". */
  deleted?: boolean;
  deletedAt?: string | null;
}

export interface TagCount {
  tag: string;
  count: number;
}

export type SessionSort = "recent" | "oldest" | "cost_desc" | "tokens_desc";

export interface ChangelogEntry {
  id: string;
  title: string;
  body: string;
  publishedAt: string;
  createdBy: string;
}

export interface OrgMember {
  id: string;
  name: string;
  email: string;
}

export type NotificationType =
  | "user_pending_approval"
  | "account_approved"
  | "invite_accepted"
  | "budget_alert"
  | "session_assigned"
  | "scheduled_prompt_ran"
  | "usage_anomaly"
  | "engine_failure";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body?: string;
  link?: string;
  createdAt: string;
  readAt: string | null;
}

export type ScheduledPromptFrequency = "daily" | "weekly";

export interface ScheduledPrompt {
  id: string;
  title: string;
  prompt: string;
  frequency: ScheduledPromptFrequency;
  weekday: number;
  hourUtc: number;
  model: string | null;
  mode: AssistantMode | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  lastRunAt: string | null;
  lastRunSessionId: string | null;
  lastError: string | null;
}

export type PromptTemplateScope = "personal" | "org";

export interface PromptTemplate {
  id: string;
  scope: PromptTemplateScope;
  title: string;
  body: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
}

export interface StatsBreakdownRow {
  costUsd: number;
  tokens: number;
  sessionCount: number;
}
export interface StatsBudget {
  limitUsdMonthly: number | null;
  spentUsdThisMonth: number;
  projectedUsdThisMonth: number;
  daysElapsed: number;
  daysInMonth: number;
}

export interface GlobalStats {
  orgId: string;
  period: { from: string; to: string };
  sessionCount: number;
  pinnedCount: number;
  totalTurns: number;
  totalUserTurns: number;
  totalAssistantTurns: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  totalCostUsd: number;
  totalDurationMs: number;
  avgDurationMs: number;
  likes: number;
  dislikes: number;
  errored: number;
  modelUsage: Record<string, number>;
  /** Origem das conversas (api vs web) - visível pra todos. */
  bySource: Record<ConversationSource, StatsBreakdownRow>;
  /** Custo/tokens por motor de IA (claude/codex) no período - visível pra todos. */
  byAgent?: Record<"claude" | "codex", { costUsd: number; tokens: number; responses: number }>;
  /** Admin/sysadmin só - breakdown por workspace/usuário da org. */
  breakdown?: {
    byWorkspace: (StatsBreakdownRow & { workspaceId: string; workspaceName: string })[];
    byUser: (StatsBreakdownRow & { userId: string; userEmail: string })[];
  };
  /** Admin/sysadmin só - orçamento/projeção do mês corrente (independe do filtro de período). */
  budget: StatsBudget | null;
  /** Admin/sysadmin só - plano/limites da org (assentos, workspaces, repos, sync). */
  plan: {
    linked: boolean;
    planId: string | null;
    status: string | null;
    seats: { count: number; limit: number | null };
    workspaces: { count: number; limit: number | null };
    repoCount: number;
    budgetUsdMonthly: number | null;
    repoSyncMinutes: number;
  } | null;
  /** Sysadmin só - orgs disponíveis pro seletor. */
  availableOrgs?: { id: string; name: string }[];
}

export type StreamEventKind =
  | "start"
  | "snapshot"
  | "token"
  | "tool_use"
  | "tool_result"
  | "done"
  | "aborted"
  | "error";

export type StreamEvent =
  | { kind: "start"; sessionId: string }
  | { kind: "snapshot"; sessionId: string; text: string; tools: Array<{ name: string; input: unknown }> }
  | { kind: "token"; text: string }
  | { kind: "tool_use"; name: string; input: unknown }
  | { kind: "tool_result"; isError: boolean }
  | { kind: "done"; durationMs?: number; totalCostUsd?: number; usage?: TurnUsage }
  | { kind: "aborted"; partial: boolean }
  | { kind: "error"; message: string };

export interface ToolEvent {
  id: string;
  name: string;
  input: unknown;
  isError?: boolean;
}

export type ColorTheme = "laranja" | "vermelho" | "azul" | "verde";

/** Identidade completa do app - shape editável na tela de administração. */
export interface BrandingConfig {
  appName: string;
  companyName: string;
  ownerName: string;
  ownerRole: string;
  workspaceLabel: string;
  workspaceOverview: string;
  customGuidance: string;
  exampleQuestionsBusiness: string[];
  exampleQuestionsTechnical: string[];
  aboutExampleQuestions: string[];
  colorTheme: ColorTheme;
  logoDataUrl: string;
}

export interface HealthInfo {
  claudeAvailable?: boolean;
  codexAvailable: boolean;
  geminiAvailable: boolean;
  status: string;
  model: string;
  workspace: string;
  workspaceMounted: boolean;
  authMode: "oauth" | "api-key" | "missing";
  appName: string;
  companyName: string;
  exampleQuestionsBusiness: string[];
  exampleQuestionsTechnical: string[];
  aboutExampleQuestions: string[];
  colorTheme: ColorTheme;
  logoDataUrl: string;
  ssoProviders: { google: boolean; microsoft: boolean; github: boolean };
  maintenanceActive: boolean;
  maintenanceMessage: string;
  maintenanceUntil: string | null;
}
